import { useCallback, useEffect, useRef, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import {
  DEFAULT_LAYERS,
  LA_FALLBACK,
  SEARCH_DEBOUNCE_MS
} from "../redux/global-map.constants"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectMapData,
  selectSearchTerm,
  setActiveLayers,
  setSearchTerm,
  setViewport
} from "../redux/global-map.slice"
import { fetchMapFeatures } from "../redux/global-map.thunks"
import {
  EMPTY_FILTERS,
  type EntityType,
  type GlobalMapFilters,
  type MapFeaturesResponse,
  type MapViewport
} from "../redux/global-map.types"
import { countBucket, emitMapEvent, zoomBucket } from "../telemetry"

type FetchMapFeaturesOutcome =
  | ReturnType<typeof fetchMapFeatures.fulfilled>
  | ReturnType<typeof fetchMapFeatures.rejected>

// Shared by every fetch call site below: reports `map_viewed` once the
// request actually lands data, or `map_error` on a real rejection — `zoom`
// is passed in separately because the mount-time fetch has no `MapViewport`
// yet (it goes out with `viewport: null`, so it has no zoom of its own to
// report beyond the server-fallback default it requested). A `meta.condition`
// rejection isn't a real error — it's `fetchMapFeatures`'s own `condition`
// silently refusing to fire at all (the "zero active layers" case; see that
// thunk's doc comment) — so it's excluded here rather than reported as one.
const reportFetchOutcome = (
  dispatched: Promise<FetchMapFeaturesOutcome>,
  zoom: number
): void => {
  void dispatched.then((action) => {
    if (fetchMapFeatures.fulfilled.match(action)) {
      emitMapEvent("map_viewed", {
        result_count_bucket: countBucket(action.payload.total),
        zoom_bucket: zoomBucket(zoom),
        mode: action.payload.mode
      })
    } else if (
      fetchMapFeatures.rejected.match(action) &&
      !action.meta.condition
    ) {
      emitMapEvent("map_error", {
        error_code: action.payload?.code ?? "unknown",
        retryable: String(action.payload?.retryable ?? false)
      })
    }
  })
}

interface UseMapFeaturesResult {
  userPanned: boolean
  handleViewportChange: (viewport: MapViewport) => void
  markManualNavigation: () => void
  resetManualNavigation: () => void
  viewAll: () => void
  clearSearch: () => void
  showFeaturesByIds: (ids: string[]) => Promise<MapFeaturesResponse | null>
}

/**
 * Owns what keeps `MapTab`'s data in sync: the initial unbounded fetch on
 * mount, a fetch on every (already-debounced, inside `MapCanvas`) viewport
 * change, a fetch on every committed filters change, and a fetch — debounced
 * on its own — on every search term change.
 *
 * `userPanned` distinguishes a real user gesture from the "idle" events a
 * camera move we *commanded* also produces — `MapCanvas` mounts centered on
 * the LA fallback before any data exists, and every auto-fit `MapTab` applies
 * while `shouldAutoFit` is true re-centers the camera again. Both fire the
 * same `onViewportChange` callback as a genuine pan, so without this guard
 * the very first (purely programmatic) idle event — reported before the
 * initial fetch has even resolved — would flip `userPanned` true and disable
 * auto-fit before it ever got to run. `expectingProgrammaticSettle` starts
 * `true` for the mount's own default-center settle, and is re-armed every
 * time a new response arrives while the user still hasn't manually panned
 * (a fresh `computeFit` result is about to be applied as `fitTo`).
 *
 * `ready` (defaults `true` for any caller that doesn't pass it, e.g. this
 * hook's own standalone tests) gates the initial fetch on
 * `useGlobalMapUrlSync`'s own mount-time URL read having completed —
 * without it, a URL carrying filter params fires this hook's mount fetch
 * once against the pre-read (default) filters and again once the real ones
 * land, wastefully doubling the heaviest query on the page (Finding 2,
 * Increment 3 final whole-branch review). `lastFetchedFiltersRef` (not a
 * "skip the first run" flag) is what then keeps the mount fetch and the
 * filters-change effect below from ALSO double-firing each other once
 * `ready` flips true and `filters` changes in the very same commit — it
 * fires only when `filters` has genuinely changed since whichever effect
 * fetched last, regardless of which of the two effects that was.
 */
export function useMapFeatures(ready: boolean = true): UseMapFeaturesResult {
  const dispatch = useAppDispatch()
  const data = useAppSelector(selectMapData)
  const viewport = useAppSelector((state) => state.globalMap.viewport)
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const activeLayers = useAppSelector(selectActiveLayers)
  const activeBranchId = useAppSelector(selectActiveBranchId) ?? undefined
  const [userPanned, setUserPanned] = useState(false)
  const expectingProgrammaticSettle = useRef(true)
  const lastArmedFingerprint = useRef<string | null>(null)
  const hasFetchedInitial = useRef(false)
  const lastFetchedFiltersRef = useRef<GlobalMapFilters | null>(null)
  // Value-comparison ref, same shape as `lastFetchedFiltersRef` above — see
  // the layer-toggle effect below for why this exists at all.
  const lastFetchedLayersRef = useRef<EntityType[] | null>(null)

  // Mount-only: the initial unbounded fetch happens exactly once, carrying
  // whatever filters/search the store already holds once `ready` — see this
  // hook's own doc comment above. Waiting on `ready` (rather than firing
  // unconditionally on `[dispatch]`, as this used to) means the filters read
  // here are already the real, URL-synced ones whenever the URL carried any,
  // not a default that a second fetch would have to correct.
  useEffect(() => {
    if (!ready || hasFetchedInitial.current) return
    hasFetchedInitial.current = true
    lastFetchedFiltersRef.current = filters
    lastFetchedLayersRef.current = activeLayers

    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport: null,
          filters,
          search: searchTerm,
          entities: activeLayers,
          team: activeBranchId
        })
      ),
      LA_FALLBACK.zoom
    )
  }, [ready, dispatch])

  useEffect(() => {
    if (userPanned || !data) return
    if (data.query_fingerprint === lastArmedFingerprint.current) return
    lastArmedFingerprint.current = data.query_fingerprint
    expectingProgrammaticSettle.current = true
  }, [data, userPanned])

  const handleViewportChange = useCallback(
    (nextViewport: MapViewport) => {
      dispatch(setViewport(nextViewport))

      const isProgrammaticSettle = expectingProgrammaticSettle.current
      if (isProgrammaticSettle) {
        expectingProgrammaticSettle.current = false
      } else {
        setUserPanned(true)
      }

      // A programmatic settle never needs its own fetch — it is either the
      // map's very first idle event, reporting the hardcoded initial center
      // it was CREATED with (the mount effect above already dispatched the
      // real, unbounded fetch this page actually needs, in the same commit
      // or earlier), or the settle that follows applying `autoFitTo`, which
      // only exists because `computeFit` already had real `data` to fit to.
      // Dispatching a fetch here anyway used to race the mount effect's own
      // unbounded one — both share the same `requestId`-wins-by-dispatch-
      // order staleness guard (`global-map.slice.ts`), so this later,
      // viewport-BOUNDED fetch (bounded to wherever the map happened to
      // start, e.g. `LA_FALLBACK`) always won and silently replaced the
      // correct unbounded result with a narrow slice of it — a real,
      // reported bug: the map got stuck showing "no results" bounded to LA
      // even though every record had already loaded successfully.
      if (isProgrammaticSettle) return

      reportFetchOutcome(
        dispatch(
          fetchMapFeatures({
            viewport: nextViewport,
            filters,
            search: searchTerm,
            entities: activeLayers,
            team: activeBranchId
          })
        ),
        nextViewport.zoom
      )
    },
    [dispatch, filters, searchTerm, activeLayers, activeBranchId]
  )

  // Refetches whenever the committed filters change — `filters` is a new
  // object reference on every `setFilters`/`clearFilters` dispatch (and only
  // then: no other action in this slice reassigns `committedQuery.filters`),
  // so an object-identity dependency fires exactly on a real filter change
  // and never on an unrelated re-render. Waits for the mount effect above to
  // have fetched at least once (`hasFetchedInitial`), and — since `ready`
  // flipping true and a URL-synced `filters` change can land in the very
  // same commit as that mount fetch — skips a `filters` value that effect
  // already fetched for (`lastFetchedFiltersRef`), rather than unconditionally
  // skipping just its own first run the way this used to: a flag keyed to
  // "which render this effect is on" can't tell a mount-time filters change
  // from a real later one, but a value comparison can.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (filters === lastFetchedFiltersRef.current) return
    const previousFilters = lastFetchedFiltersRef.current
    lastFetchedFiltersRef.current = filters

    // A committed filter change — narrowing (picking a subcontractor) or
    // widening (Clear All) — is exactly as strong an intent signal as
    // dismissing the quick view (`resetManualNavigation`'s own call site in
    // `MapTab.tsx`): the user asked to see a specific set of records, so the
    // camera should frame whatever that set is now, even if they'd panned
    // away from the auto-fit view earlier in the session. Without this, a
    // single prior pan (or a marker click, via `markManualNavigation`)
    // permanently disabled `autoFitTo` (`shouldAutoFit` is just
    // `!userPanned`) for the rest of the session — a real, reported bug:
    // picking a filter still narrowed which markers were fetched, so it
    // "worked" as long as the current viewport happened to contain them, but
    // clearing that filter again never zoomed back out to show everything
    // else it now matched.
    setUserPanned(false)

    // The backend's `/features` marker list ANDs the near-zip/radius filter
    // with the current viewport bounds (`GlobalMapQuery.bounded_queryset`) —
    // it's a marker list, not just a count. So a bounded fetch here is fine
    // for an ordinary filter (Subcontractor, Status, ...), which never
    // changes what area of the map means "in view" — but the near-radius
    // filter IS the area of interest: dragging its slider from 5 to 50 miles
    // while still fetching bounded to the OLD (5-mile-sized) viewport
    // intersects the new radius down to the old one, so nothing outside it
    // can ever appear and the map visibly does nothing. A real, reported
    // bug: "the radius does not change when I change the radius from 1 mile
    // to 50 mile". Fetching unbounded whenever the zip or radius itself
    // changed lets `autoFitTo` (armed by `setUserPanned(false)` above) zoom
    // the camera to match the new radius, the same escape hatch
    // `viewAll`/`clearSearch` already use for the identical reason.
    const nearFilterChanged =
      previousFilters?.nearZip !== filters.nearZip ||
      previousFilters?.nearRadiusMi !== filters.nearRadiusMi
    const fetchViewport = nearFilterChanged ? null : viewport

    // Fetches for whatever viewport is currently live (or unbounded, for a
    // near-radius change — see above), so that viewport becomes the new
    // committed baseline — a filter change resyncs the data to the current
    // view.
    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport: fetchViewport,
          filters,
          search: searchTerm,
          entities: activeLayers,
          team: activeBranchId
        })
      ),
      fetchViewport?.zoom ?? LA_FALLBACK.zoom
    )
  }, [filters, dispatch])

  // Search needs its own debounce, separate from `VIEWPORT_DEBOUNCE_MS` — a
  // pan is already debounced once, upstream, inside `MapCanvas` before
  // `handleViewportChange` ever runs, while every keystroke here reaches
  // this hook directly via `setSearchTerm`. Typing "Rivera" must fire one
  // request, not six: each keystroke re-runs this effect, and the cleanup
  // from the previous run cancels its pending timeout before the new one is
  // armed, so only the timer from the last keystroke in a burst ever fires.
  //
  // `lastFetchedSearchRef` (a value-comparison ref, not a "skip the first
  // run" flag) mirrors `lastFetchedFiltersRef` above for the identical
  // reason: `clearSearch`/`viewAll` below need to pre-arm this effect to
  // treat an upcoming `searchTerm` change as already handled, the same way
  // `viewAll` already pre-arms the filters effect — otherwise a caller that
  // clears BOTH filters and search in one click (Clear All/View All) would
  // get a second, redundant fetch from this effect racing the first one,
  // the exact bug class `viewAll`'s own doc comment describes for filters.
  // Starting it at `searchTerm`'s own initial value (always `""` — search
  // is deliberately not URL-synced, see `GlobalMapCommittedQuery`'s own
  // comment) skips the first run for free, the same way
  // `lastFetchedFiltersRef` does by being seeded from the mount effect.
  const lastFetchedSearchRef = useRef(searchTerm)
  useEffect(() => {
    if (searchTerm === lastFetchedSearchRef.current) return
    lastFetchedSearchRef.current = searchTerm
    // Same reasoning as the filters effect's own `setUserPanned(false)`
    // above: a committed search change (narrowing OR clearing) is exactly
    // as strong an intent signal as a filter change, so it re-engages
    // `autoFitTo` too. Without this, clearing a search term after it had
    // auto-fitted the camera in tight to a narrow result set left the
    // resync fetch itself BOUNDED to that same tiny (still zoomed-in)
    // viewport — a real, reported bug: "Clear All" correctly cleared the
    // search's own filter state but the map kept showing only the one
    // record the search had zoomed to, since nothing ever told the camera
    // it was free to zoom back out before the resync fetch went out.
    setUserPanned(false)
    const handle = setTimeout(() => {
      reportFetchOutcome(
        dispatch(
          fetchMapFeatures({
            viewport,
            filters,
            search: searchTerm,
            entities: activeLayers,
            team: activeBranchId
          })
        ),
        viewport?.zoom ?? LA_FALLBACK.zoom
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchTerm, dispatch])

  // Refetches whenever the "Projects & Leads" layer toggle changes —
  // 2026-09-19: this used to be a client-side-only filter over an
  // always-both-entity fetch (see `filterByActiveLayers` in `MapTab.tsx`),
  // which is why the KPI totals stayed frozen when a layer was switched off
  // even after the backend itself became entities-aware — no request ever
  // actually carried the toggle's state. Mirrors the filters effect above
  // exactly, including the `lastFetchedLayersRef` value-comparison guard
  // (`clearSearch`/`viewAll` pre-arm it the same way they already pre-arm
  // `lastFetchedFiltersRef`/`lastFetchedSearchRef`, so their own explicit
  // fetch below isn't immediately raced by a second, redundant one from
  // this effect reacting to the `setActiveLayers` they also dispatch).
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (activeLayers === lastFetchedLayersRef.current) return
    lastFetchedLayersRef.current = activeLayers

    setUserPanned(false)
    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport,
          filters,
          search: searchTerm,
          entities: activeLayers,
          team: activeBranchId
        })
      ),
      viewport?.zoom ?? LA_FALLBACK.zoom
    )
  }, [activeLayers, dispatch])

  // Refetches whenever the selected branch workspace changes — mirrors the
  // layer-toggle effect above exactly (same value-comparison guard shape),
  // since switching branches is the same kind of population change a layer
  // toggle is: the currently-loaded markers may no longer belong to what's
  // selected, so the camera is freed to re-fit rather than staying pinned to
  // wherever it happened to be framed for the old branch.
  const lastFetchedBranchIdRef = useRef(activeBranchId)
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (activeBranchId === lastFetchedBranchIdRef.current) return
    lastFetchedBranchIdRef.current = activeBranchId

    setUserPanned(false)
    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport: null,
          filters,
          search: searchTerm,
          entities: activeLayers,
          team: activeBranchId
        })
      ),
      LA_FALLBACK.zoom
    )
  }, [activeBranchId, dispatch])

  // "Clear All" — clears search AND filters (its caller, `GlobalMapFilterBar`,
  // always dispatches `clearFilters()` in the very same click) via its own
  // explicit unbounded fetch, the identical shape `viewAll` below uses and
  // for the identical reason: a bare `dispatch(setSearchTerm(""))` alone
  // went through two broken iterations before this one, both confirmed live,
  // not just reasoned about —
  //   1. No pre-arm at all: correctly let the search effect's own fetch
  //      fire, but that fetch is BOUNDED to whatever viewport is currently
  //      live — and if search had already auto-fitted the camera in tight
  //      to a narrow result (exactly what committing a search does), the
  //      "cleared" fetch only asked for what was inside that same tiny
  //      area, so the map stayed stuck showing just the old narrow result
  //      even though the search/filter STATE was genuinely cleared.
  //   2. Pre-arming `lastFetchedSearchRef` the way `viewAll` does, but with
  //      no replacement fetch of its own: suppressed the ONLY fetch that
  //      would have run at all, leaving the map on the stale result forever.
  // An explicit unbounded fetch (`viewport: null`) is what actually escapes
  // a narrow committed viewport, the same fix `viewAll`'s own doc comment
  // describes for the filters side of this exact bug class. `filters` is
  // hardcoded to `EMPTY_FILTERS` (not the closure's current `filters`,
  // which is still the PRE-clear value here — `clearFilters()` and this run
  // synchronously in the same click, before either's dispatch has caused a
  // re-render) — safe only because this function's one real caller always
  // pairs it with `clearFilters()` in that same click; it would need
  // rethinking if `clearSearch` ever gets a caller that isn't also clearing
  // every filter.
  const clearSearch = useCallback(() => {
    lastFetchedFiltersRef.current = EMPTY_FILTERS
    lastFetchedSearchRef.current = ""
    lastFetchedLayersRef.current = DEFAULT_LAYERS
    setUserPanned(false)
    dispatch(setSearchTerm(""))
    dispatch(setActiveLayers(DEFAULT_LAYERS))
    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport: null,
          filters: EMPTY_FILTERS,
          search: "",
          entities: DEFAULT_LAYERS
        })
      ),
      LA_FALLBACK.zoom
    )
  }, [dispatch])

  // A manual camera command the user explicitly triggered — recentering on a
  // project from the quick view, or expanding a cluster — is manual
  // exploration every bit as much as dragging the map is, even though it
  // never touches `onViewportChange`. It has to flip `userPanned` itself:
  // otherwise `autoFitTo` (memoized on `[data, userPanned]` in `MapTab`)
  // stays the same pre-click value, and falling back to it once the one-shot
  // manual fit clears snaps the camera straight back to the wide auto-fit
  // view on the very next render.
  const markManualNavigation = useCallback(() => {
    setUserPanned(true)
  }, [])

  // The inverse of `markManualNavigation` — once the user has panned, or
  // clicked a marker (which now recenters the camera to street-level zoom),
  // `autoFitTo` (in `MapTab`, memoized on `[filteredData, userPanned]`)
  // permanently stops recomputing, so every other record the current
  // filters still match stays out of view with no way back short of a
  // manual zoom-out. Called from `handleCloseQuickView` so dismissing a
  // selection is what hands the camera back to "fit everything currently
  // matching the filters" — `shouldAutoFit(null, userPanned)` is just
  // `!userPanned`, so this alone is enough to make `autoFitTo` recompute a
  // real fit-bounds on the very next render.
  const resetManualNavigation = useCallback(() => {
    setUserPanned(false)
  }, [])

  // "View All" — same real bug `handleCloseQuickView` (`MapTab.tsx`) already
  // documents, hit again from a different trigger: `resetManualNavigation()`
  // alone re-enables `autoFitTo`, but `autoFitTo` only ever fits to whatever
  // `filteredData` currently holds — and if the user had previously clicked
  // a marker, that recenter's own settle already narrowed `data` to a
  // BOUNDED fetch (whatever tiny handful of records survived that
  // street-level viewport). Resetting `userPanned` alone just re-fits
  // *tightly to that same narrow set*, not "every record" — an unbounded
  // refetch (`viewport: null`) is what actually gets the full set back.
  //
  // The caller (`GlobalMapFilterBar`'s "View All" button) also dispatches
  // `clearFilters()` (and now `clearSearch()`) in the same click handler,
  // which independently changes `filters`/`searchTerm` — the two effects
  // above react to that on their own and would each dispatch a SECOND,
  // BOUNDED fetch (bounded to whatever viewport is still live) that always
  // wins the `requestId`-by-dispatch-order staleness race against this
  // function's own unbounded one, since effects run after this synchronous
  // call returns. Pre-arming `lastFetchedFiltersRef`/`lastFetchedSearchRef`
  // to the exact values `clearFilters()`/`clearSearch()` themselves write
  // makes both effects' own guards see their work as already done and skip
  // firing those competing fetches — the same "suppress the redundant one
  // instead of racing it" approach `handleViewportChange`'s
  // `isProgrammaticSettle` guard already uses. "View all" means "no
  // filtering" per its own product definition, so this always searches for
  // "" regardless of whatever was still typed in the search box, rather
  // than reusing the (possibly stale, possibly non-empty) `searchTerm`
  // closure value a caller might expect to be preserved.
  const viewAll = useCallback(() => {
    lastFetchedFiltersRef.current = EMPTY_FILTERS
    lastFetchedSearchRef.current = ""
    lastFetchedLayersRef.current = DEFAULT_LAYERS
    setUserPanned(false)
    dispatch(setSearchTerm(""))
    dispatch(setActiveLayers(DEFAULT_LAYERS))
    reportFetchOutcome(
      dispatch(
        fetchMapFeatures({
          viewport: null,
          filters: EMPTY_FILTERS,
          search: "",
          entities: DEFAULT_LAYERS
        })
      ),
      LA_FALLBACK.zoom
    )
  }, [dispatch])

  // The List tab's "Show on Map" bulk action — an explicit, caller-picked
  // set of record ids, shown regardless of the current viewport/filters/
  // layer toggle (the backend's own `ids` param ignores all three; see
  // `MapFeaturesView.get`'s doc comment). Widens `activeLayers` to
  // `DEFAULT_LAYERS` (never narrows it — a selection can span both
  // entities) so `filterByActiveLayers` (`MapTab.tsx`) never client-side
  // hides a returned marker the server correctly included; pre-arms
  // `lastFetchedLayersRef` the same way `viewAll`/`clearSearch` already do,
  // so the layers-change effect above doesn't immediately race this with a
  // second, normal (non-`ids`) fetch that would overwrite the result.
  // Returns the fetched payload (or `null` on failure/empty selection) so
  // the caller — `MapTab.handleShowSelectionOnMap` — can compute a camera
  // fit over exactly these records; unlike every other fetch in this hook,
  // that fit is the CALLER's job (`manualFit` is `MapTab`'s own local
  // state, not something this hook can set), the same division of labor
  // `markManualNavigation`/`setManualFit` already have for
  // `handleRecenter`/`handleClusterClick`.
  const showFeaturesByIds = useCallback(
    async (ids: string[]): Promise<MapFeaturesResponse | null> => {
      if (!ids.length) return null

      lastFetchedLayersRef.current = DEFAULT_LAYERS
      dispatch(setActiveLayers(DEFAULT_LAYERS))

      const dispatched = dispatch(
        fetchMapFeatures({
          viewport: null,
          filters: EMPTY_FILTERS,
          search: "",
          entities: DEFAULT_LAYERS,
          ids
        })
      )
      reportFetchOutcome(dispatched, LA_FALLBACK.zoom)

      const action = await dispatched
      return fetchMapFeatures.fulfilled.match(action) ? action.payload : null
    },
    [dispatch]
  )

  return {
    userPanned,
    handleViewportChange,
    markManualNavigation,
    resetManualNavigation,
    viewAll,
    clearSearch,
    showFeaturesByIds
  }
}
